import i18n from "../i18n/config";
import { normalizeApiErrorBody } from "./channelSaveError";
import { getUserFacingApiMessage } from "./getUserFacingApiMessage";

function t(key, options) {
  return i18n.t(key, { ns: "settings", ...options });
}

function joinMessageAndAction(main, action) {
  const m = String(main || "").trim();
  const a = String(action || "").trim();
  if (m && a) return `${m}\n\n${a}`;
  return m || a || t("contentsPage.saveErrorGeneric");
}

function extractStatus(error) {
  if (error?.status != null && Number.isFinite(Number(error.status))) {
    return Number(error.status);
  }
  if (error?.response?.status != null) return Number(error.response.status);
  return null;
}

function isNetworkError(error) {
  if (error?.response) return false;
  const code = error?.code;
  if (code === "ERR_NETWORK" || code === "ECONNABORTED") return true;
  const msg = String(error?.message || "");
  return /network error/i.test(msg);
}

/** Mensagens exatas retornadas pela API de conteúdos (pt-BR). */
const BY_SERVER_MESSAGE = {
  "Informe o título.": {
    main: "contentsPage.saveErrorTitle",
    action: "contentsPage.saveErrorTitleAction",
  },
  "Informe o slug.": {
    main: "contentsPage.saveErrorSlug",
    action: "contentsPage.saveErrorSlugAction",
  },
  "Já existe um conteúdo com este tipo e slug.": {
    main: "contentsPage.saveErrorSlugDuplicate",
    action: "contentsPage.saveErrorSlugDuplicateAction",
  },
  "ID inválido.": {
    main: "contentsPage.saveErrorInvalidId",
    action: "contentsPage.saveErrorInvalidIdAction",
  },
  "Conteúdo não encontrado.": {
    main: "contentsPage.saveErrorNotFound",
    action: "contentsPage.saveErrorNotFoundAction",
  },
  "Nenhum campo válido para atualizar.": {
    main: "contentsPage.saveErrorNoFields",
    action: "contentsPage.saveErrorNoFieldsAction",
  },
  "Erro ao criar conteúdo.": {
    main: "contentsPage.saveErrorServer",
    action: "contentsPage.saveErrorServerAction",
  },
  "Erro ao atualizar conteúdo.": {
    main: "contentsPage.saveErrorServer",
    action: "contentsPage.saveErrorServerAction",
  },
};

const BY_CODE = {
  "contents.title_required": {
    main: "contentsPage.saveErrorTitle",
    action: "contentsPage.saveErrorTitleAction",
  },
  "contents.slug_required": {
    main: "contentsPage.saveErrorSlug",
    action: "contentsPage.saveErrorSlugAction",
  },
  "contents.slug_duplicate": {
    main: "contentsPage.saveErrorSlugDuplicate",
    action: "contentsPage.saveErrorSlugDuplicateAction",
  },
  "contents.invalid_id": {
    main: "contentsPage.saveErrorInvalidId",
    action: "contentsPage.saveErrorInvalidIdAction",
  },
  "contents.not_found": {
    main: "contentsPage.saveErrorNotFound",
    action: "contentsPage.saveErrorNotFoundAction",
  },
  "contents.no_fields": {
    main: "contentsPage.saveErrorNoFields",
    action: "contentsPage.saveErrorNoFieldsAction",
  },
};

/**
 * Mensagem completa para falha ao salvar conteúdo (POST/PUT) e validações do formulário.
 * @param {unknown} error
 * @param {{ slug?: string, tipo?: string }} [ctx]
 */
export function buildContentSaveErrorMessage(error, ctx = {}) {
  if (isNetworkError(error)) {
    return joinMessageAndAction(
      t("contentsPage.saveErrorNetwork"),
      t("contentsPage.saveErrorNetworkAction")
    );
  }

  const status = extractStatus(error);
  if (status === 429) {
    return joinMessageAndAction(
      t("contentsPage.saveErrorRateLimit"),
      t("contentsPage.saveErrorRateLimitAction")
    );
  }

  const raw = normalizeApiErrorBody(error);
  const u = getUserFacingApiMessage(error);
  const code = typeof raw.code === "string" ? raw.code : null;
  const params =
    raw.params && typeof raw.params === "object"
      ? { ...raw.params, slug: ctx.slug ?? raw.params.slug, tipo: ctx.tipo ?? raw.params.tipo }
      : { slug: ctx.slug, tipo: ctx.tipo };

  const codeMap = code ? BY_CODE[code] : null;
  const slugLabel =
    params.slug && String(params.slug).trim()
      ? String(params.slug).trim()
      : ctx.slug && String(ctx.slug).trim()
        ? String(ctx.slug).trim()
        : "—";

  const i18nParams = { ...params, slug: slugLabel, tipo: params.tipo || ctx.tipo || "" };

  if (codeMap) {
    return joinMessageAndAction(
      t(codeMap.main, i18nParams),
      t(codeMap.action, i18nParams)
    );
  }

  const serverMessage =
    (typeof raw.message === "string" && raw.message.trim()) ||
    (typeof u.message === "string" && u.message.trim()) ||
    null;

  const msgMap = serverMessage ? BY_SERVER_MESSAGE[serverMessage] : null;
  if (msgMap) {
    return joinMessageAndAction(
      t(msgMap.main, i18nParams),
      t(msgMap.action, i18nParams)
    );
  }

  if (u.action) {
    return joinMessageAndAction(u.message, u.action);
  }

  if (status != null && status >= 500) {
    const detail =
      typeof raw.data === "string" && raw.data.trim() ? raw.data.trim() : null;
    return joinMessageAndAction(
      t("contentsPage.saveErrorServer"),
      detail
        ? t("contentsPage.saveErrorServerActionWithDetail", { detail })
        : t("contentsPage.saveErrorServerAction")
    );
  }

  if (serverMessage) {
    return joinMessageAndAction(
      serverMessage,
      t("contentsPage.saveErrorGenericAction")
    );
  }

  return joinMessageAndAction(
    t("contentsPage.saveErrorGeneric"),
    t("contentsPage.saveErrorGenericAction")
  );
}

/** Validações locais do formulário (antes de chamar a API). */
export function buildContentClientValidationMessage(key, params = {}) {
  const map = {
    title: {
      main: "contentsPage.saveErrorTitle",
      action: "contentsPage.saveErrorTitleAction",
    },
    slug: {
      main: "contentsPage.saveErrorSlug",
      action: "contentsPage.saveErrorSlugAction",
    },
    trainingModules: {
      main: "contentForm.validationTrainingModules",
      action: "contentForm.validationTrainingModulesAction",
    },
    instagramMeta: {
      main: "contentForm.validationInstagramMeta",
      action: "contentForm.validationInstagramMetaAction",
    },
    ordemInvalid: {
      main: "contentForm.validationOrdemInvalid",
      action: "contentForm.validationOrdemInvalidAction",
    },
  };
  const entry = map[key];
  if (!entry) return t("contentsPage.saveErrorGeneric");
  return joinMessageAndAction(t(entry.main, params), t(entry.action, params));
}
